import { cva } from '@styled-system/css';
import { useState } from 'react';

const sizeMap = {
  xs: 20,
  sm: 24,
  md: 32,
  lg: 40,
  xl: 56,
} as const;

export type AvatarSize = keyof typeof sizeMap;

const avatar = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'full',
    overflow: 'hidden',
    flexShrink: 0,
    bg: 'surface.sunken',
    color: 'text.secondary',
    fontFamily: 'sans',
    fontWeight: 'semibold',
    textTransform: 'uppercase',
    userSelect: 'none',
  },
});

const fontFor = (px: number) => Math.max(10, Math.round(px * 0.42));

const initials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2);
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
};

interface AvatarProps {
  name?: string;
  src?: string;
  size?: AvatarSize;
  className?: string;
  alt?: string;
}

export const Avatar = ({ name, src, size = 'md', className, alt }: AvatarProps) => {
  const [errored, setErrored] = useState(false);
  const px = sizeMap[size];
  const showImg = src && !errored;
  const text = name ? initials(name) : '?';
  return (
    <span
      className={`${avatar()} ${className ?? ''}`}
      style={{ width: `${px}px`, height: `${px}px`, fontSize: `${fontFor(px)}px` }}
      aria-label={alt ?? name}
    >
      {showImg ? (
        <img
          src={src}
          alt={alt ?? name ?? ''}
          onError={() => setErrored(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        text
      )}
    </span>
  );
};
